import { ScissorOutlined, UploadOutlined } from '@ant-design/icons'
import React, { useRef } from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { TextArea } from 'sutra/components/Input/TextArea'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'

import { AttachmentList } from 'features/bug-report/components/AttachmentList'
import { ACCEPTED_UPLOAD_TYPES } from 'features/bug-report/constants'
import type { Attachment } from 'features/bug-report/types'

type Props = {
  open: boolean
  message: string
  onMessageChange: React.ChangeEventHandler<HTMLTextAreaElement>
  onMessageBlur: React.FocusEventHandler<HTMLTextAreaElement>
  errorMessage: string | null
  isSubmitting: boolean
  attachments: Attachment[]
  onSubmit: React.FormEventHandler<HTMLFormElement>
  onCancel: () => void
  onTakeScreenshot: () => void
  onUploadFiles: (files: File[]) => void
  onRemoveAttachment: (id: string) => void
}

export const ReportBugModal: React.FC<Props> = ({
  open,
  message,
  onMessageChange,
  onMessageBlur,
  errorMessage,
  isSubmitting,
  attachments,
  onSubmit,
  onCancel,
  onTakeScreenshot,
  onUploadFiles,
  onRemoveAttachment,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleUploadClick = (): void => {
    fileInputRef.current?.click()
  }

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    onUploadFiles(files)
  }

  return (
    <PopoverModal
      title="Report a bug"
      isOpen={open}
      onClose={onCancel}
      footer={null}
      width={520}
      content={
        <form onSubmit={onSubmit}>
          <TextArea
            name="message"
            rows={5}
            placeholder="What did you expect, and what happened instead?"
            value={message}
            onChange={onMessageChange}
            onBlur={onMessageBlur}
            disabled={isSubmitting}
          />
          {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
          <AttachmentList
            attachments={attachments}
            onRemove={onRemoveAttachment}
          />
          <BottomRow>
            <Button
              variant="secondary"
              onClick={onTakeScreenshot}
              disabled={isSubmitting}
              icon={<ScissorOutlined />}
            >
              Take screenshot
            </Button>
            <Button
              variant="secondary"
              onClick={handleUploadClick}
              disabled={isSubmitting}
              icon={<UploadOutlined />}
            >
              Upload file
            </Button>
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_UPLOAD_TYPES}
              multiple
              onChange={handleFileChange}
            />
            <Spacer />
            <Button variant="text" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={isSubmitting}>
              Send report
            </Button>
          </BottomRow>
        </form>
      }
    />
  )
}

const ErrorText = styled.div`
  color: #d4380d;
  font-size: 12px;
  margin-top: 4px;
`

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  flex-wrap: wrap;
`

const Spacer = styled.div`
  flex: 1;
`

const HiddenInput = styled.input`
  display: none;
`
